import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Save, X, Plus, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { updateFinancialSummary, updateMerchantLearning, isBusinessExpense as checkIsBusinessExpense } from "@/lib/receiptUtils";
import { calculateTaxSavings } from "@/lib/taxRules";
import { normalizeMerchantName } from "@/lib/merchantNormalizer";
import { CATEGORIES } from "@/lib/constants";
import { validateReceiptData } from "@/lib/receiptValidation";

interface LineItem {
  name: string;
  quantity: number;
  price: number;
  category?: string;
  isDeductible?: boolean;
}

export default function ReceiptEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [merchant, setMerchant] = useState("");
  const [address, setAddress] = useState("");
  const [date, setDate] = useState("");
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [subtotal, setSubtotal] = useState(0);
  const [tax, setTax] = useState(0);
  const [total, setTotal] = useState(0);
  const [currency, setCurrency] = useState("CAD");
  const [isBusinessExpense, setIsBusinessExpense] = useState(true);
  const [originalData, setOriginalData] = useState<any>(null);
  const [userSettings, setUserSettings] = useState<any>(null);

  useEffect(() => {
    fetchReceipt();
    fetchUserSettings();
  }, [id]);

  useEffect(() => {
    // Auto-calculate totals
    const newSubtotal = lineItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    setSubtotal(newSubtotal);
    const newTotal = newSubtotal + tax;
    setTotal(newTotal);
  }, [lineItems, tax]);

  const fetchUserSettings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from("profiles")
        .select("currency, tax_deduction_rate, meal_deduction_rate")
        .eq("id", user.id)
        .maybeSingle();

      setUserSettings(data);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Error fetching user settings:", error);
      }
      toast({
        title: "Warning",
        description: "Could not load user settings. Using defaults.",
        variant: "destructive",
      });
    }
  };

  const fetchReceipt = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/");
        return;
      }

      const { data, error } = await supabase
        .from("receipts")
        .select("*")
        .eq("id", id)
        .eq("user_id", user.id) // Security: user_id filter
        .single();

      if (error) throw error;

      setOriginalData(data);
      setMerchant(data.merchant_name || "");
      
      const parsedJson = data.parsed_json as any;
      setAddress(parsedJson?.store_address || "");
      setDate(data.date);
      setCurrency(data.currency || "CAD");
      
      // Parse line items
      const items = (data.line_items as any) || [];
      const categories = (data.item_categories as any) || [];
      const deductible = (data.deductible_items as any) || [];
      
      const enrichedItems = Array.isArray(items) ? items.map((item: any, index: number) => ({
        name: item.name || "",
        quantity: item.quantity || 1,
        price: item.price || 0,
        category: Array.isArray(categories) ? categories[index] || "Other" : "Other",
        isDeductible: Array.isArray(deductible) && item.name ? deductible.includes(item.name) : false
      })) : [];
      
      setLineItems(enrichedItems.length > 0 ? enrichedItems : [{ name: "", quantity: 1, price: 0, category: "Other", isDeductible: false }]);
      setSubtotal(data.subtotal || 0);
      setTax(data.tax || 0);
      setTotal(data.total || 0);
      
      // Determine if business expense
      const assessment = parsedJson?.business_expense_assessment;
      setIsBusinessExpense(assessment?.overall_status !== "no");
      
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
      navigate("/receipts");
    } finally {
      setLoading(false);
    }
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { name: "", quantity: 1, price: 0, category: "Other", isDeductible: false }]);
  };

  const removeLineItem = (index: number) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const updateLineItem = (index: number, field: keyof LineItem, value: any) => {
    const updated = [...lineItems];
    updated[index] = { ...updated[index], [field]: value };
    setLineItems(updated);
  };

  const handleSave = async () => {
    if (!merchant || !date) {
      toast({
        title: "Validation Error",
        description: "Merchant name and date are required",
        variant: "destructive"
      });
      return;
    }

    setSaving(true);
    
    // Save original receipt data for potential rollback
    const rollbackData = originalData ? { ...originalData } : null;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Calculate tax savings
      const deductibleItems = lineItems.filter(item => item.isDeductible).map(item => item.name);
      const nonDeductibleItems = lineItems.filter(item => !item.isDeductible).map(item => item.name);
      const categories = lineItems.map(item => item.category || "Other");
      
      const taxRate = userSettings?.tax_deduction_rate || 25;
      const mealRate = userSettings?.meal_deduction_rate || 50;
      
      let taxSavings = 0;
      lineItems.forEach((item, index) => {
        if (item.isDeductible) {
          taxSavings += calculateTaxSavings(
            item.price * item.quantity,
            item.category || "Other",
            taxRate,
            mealRate
          );
        }
      });

      // Normalize merchant name for consistent storage
      const normalizedMerchantName = normalizeMerchantName(merchant);
      const oldNormalizedMerchant = normalizeMerchantName(originalData.merchant_name);
      const merchantChanged = normalizedMerchantName !== oldNormalizedMerchant;

      // Update receipt
      const { error } = await supabase
        .from("receipts")
        .update({
          merchant_name: normalizedMerchantName,
          date,
          subtotal,
          tax,
          total,
          currency,
          line_items: lineItems.map(({ name, quantity, price }) => ({ name, quantity, price })),
          item_categories: categories,
          deductible_items: deductibleItems,
          non_deductible_items: nonDeductibleItems,
          tax_savings_estimate: taxSavings,
          parsed_json: {
            ...originalData.parsed_json,
            store_address: address,
            business_expense_assessment: {
              overall_status: isBusinessExpense ? "yes" : "no",
              qualifying_items: deductibleItems,
              non_qualifying_items: nonDeductibleItems
            }
          }
        })
        .eq("id", id)
        .eq("user_id", user.id); // Security: user_id filter

      if (error) throw error;

      // Update merchant counters if merchant changed
      if (merchantChanged) {
        // Decrement old merchant
        if (originalData.merchant_name) {
          await supabase.rpc('decrement_merchant_counter', {
            p_merchant_name: originalData.merchant_name,
            p_user_id: user.id
          });
        }
        
        // Increment new merchant (or create if doesn't exist)
        await supabase
          .from('merchants')
          .upsert({
            user_id: user.id,
            merchant_name: merchant,
            normalized_name: normalizedMerchantName,
            first_seen: new Date().toISOString(),
            last_seen: new Date().toISOString(),
            total_receipts: 1,
            common_categories: categories
          }, {
            onConflict: 'user_id,normalized_name',
            ignoreDuplicates: false
          });
      }

      // Update merchant learning with new categories
      await updateMerchantLearning(normalizedMerchantName, categories);

      // Update financial summaries with rollback logic
      // CRITICAL: Wrapped in try/catch with rollback
      try {
        // First subtract old values using helper
        const oldIsBusiness = checkIsBusinessExpense(originalData.parsed_json);
        await updateFinancialSummary(
          originalData.date,
          originalData.total,
          oldIsBusiness,
          originalData.tax_savings_estimate || 0,
          'subtract'
        );

        // Then add new values
        await updateFinancialSummary(
          date,
          total,
          isBusinessExpense,
          taxSavings,
          'add'
        );
      } catch (summaryError: any) {
        // ROLLBACK: Restore original receipt data
        if (rollbackData) {
          await supabase
            .from("receipts")
            .update({
              merchant_name: rollbackData.merchant_name,
              date: rollbackData.date,
              subtotal: rollbackData.subtotal,
              tax: rollbackData.tax,
              total: rollbackData.total,
              currency: rollbackData.currency,
              line_items: rollbackData.line_items,
              item_categories: rollbackData.item_categories,
              deductible_items: rollbackData.deductible_items,
              non_deductible_items: rollbackData.non_deductible_items,
              tax_savings_estimate: rollbackData.tax_savings_estimate,
              parsed_json: rollbackData.parsed_json,
            })
            .eq("id", id)
            .eq("user_id", user.id);
        }
        
        if (process.env.NODE_ENV !== "production") {
          console.error("Failed to update financial summary:", summaryError);
        }
        throw new Error(`Failed to update summaries. Changes have been rolled back.`);
      }

      toast({
        title: "Success",
        description: "Receipt updated successfully"
      });

      navigate(`/receipt/${id}`);
    } catch (error: any) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Failed to save receipt:", error);
      }
      toast({
        title: "Error",
        description: "Failed to update receipt",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Edit Receipt</h1>
          <p className="text-muted-foreground mt-1">Update receipt details and categorization</p>
        </div>
        <Button
          onClick={() => navigate(`/receipt/${id}`)}
          variant="ghost"
          size="icon"
        >
          <X className="h-5 w-5" />
        </Button>
      </div>

      <div className="space-y-6">
        {/* Basic Info */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Basic Information</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="merchant">Merchant Name *</Label>
              <Input
                id="merchant"
                value={merchant}
                onChange={(e) => setMerchant(e.target.value)}
                placeholder="e.g., Walmart"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="date">Date *</Label>
              <Input
                id="date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="address">Store Address</Label>
              <Input
                id="address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="123 Main St, City, State"
              />
            </div>
          </div>
        </Card>

        {/* Line Items */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Line Items</h2>
            <Button onClick={addLineItem} size="sm" variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          </div>
          
          <div className="space-y-4">
            {lineItems.map((item, index) => (
              <div key={index} className="p-4 border rounded-lg space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 grid gap-3 md:grid-cols-4">
                    <div className="md:col-span-2 space-y-2">
                      <Label>Item Name</Label>
                      <Input
                        value={item.name}
                        onChange={(e) => updateLineItem(index, "name", e.target.value)}
                        placeholder="Item description"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Quantity</Label>
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateLineItem(index, "quantity", parseInt(e.target.value) || 1)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Price</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={item.price}
                        onChange={(e) => updateLineItem(index, "price", parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  </div>
                  {lineItems.length > 1 && (
                    <Button
                      onClick={() => removeLineItem(index)}
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select
                      value={item.category}
                      onValueChange={(value) => updateLineItem(index, "category", value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tax Deductible</Label>
                    <div className="flex items-center space-x-2 h-10">
                      <Switch
                        checked={item.isDeductible}
                        onCheckedChange={(checked) => updateLineItem(index, "isDeductible", checked)}
                      />
                      <span className="text-sm text-muted-foreground">
                        {item.isDeductible ? "Yes" : "No"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Totals */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Totals</h2>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Subtotal</Label>
              <div className="text-2xl font-bold text-foreground">
                {currency} {subtotal.toFixed(2)}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="tax">Tax</Label>
              <Input
                id="tax"
                type="number"
                step="0.01"
                min="0"
                value={tax}
                onChange={(e) => setTax(parseFloat(e.target.value) || 0)}
              />
            </div>
            <div className="space-y-2">
              <Label>Total</Label>
              <div className="text-2xl font-bold text-primary">
                {currency} {total.toFixed(2)}
              </div>
            </div>
          </div>
        </Card>

        {/* Business Expense */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Business Expense Classification</h2>
          <div className="flex items-center space-x-3">
            <Switch
              checked={isBusinessExpense}
              onCheckedChange={setIsBusinessExpense}
            />
            <div>
              <Label>This is a business expense</Label>
              <p className="text-sm text-muted-foreground">
                Mark this receipt as a business expense for tax purposes
              </p>
            </div>
          </div>
        </Card>

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <Button
            onClick={() => navigate(`/receipt/${id}`)}
            variant="outline"
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#22c25c] hover:bg-[#1ea550] text-white"
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
